import { useMemo } from 'react';
import Skeleton from '@mui/material/Skeleton';
import { useScorm } from '../../../../lib/context/ScormContext';
import Layout from '../../../common/Layout/Layout';
import TableManager from '../../base/TableManager/TableManager';
import { DOWNLOADS_LIST_HEADINGS } from '../../../../lib/constants/courses';
import { mapDownloadItemToTableRow } from './DownloadsList.utils';
import { useDevice } from '../../../../lib/hooks/device';
import { DownloadsListProps } from './DownloadsList.types';
import { sanitizeText } from '../../../../lib/utils/sanitizeText.utils';
import { replacePlaceholder } from '../../../../lib/utils/placeholders/replacePlaceholder';
import PureRichText from '../../../common/PureRichText/PureRichText';
import styles from './DownloadsList.module.scss';
import PureHeading from '../../../common/Heading/PureHeading';
import { HeadingLevel } from '../../../../lib/utils/HeadingMapper';
import RefreshLoader from '../../../common/RefreshLoader/RefreshLoader';
import BasicBanner from '../../base/BasicBanner/BasicBanner';
import ContentBlockView from '../../../common/ContentBlockView/ContentBlockView';
import { downloadsListIds } from '../../../../lib/tests/testIds';
import { useId } from '../../../../lib/hooks/useId';
import ScormDownloadButton from '../../base/ScormDownloadButton/ScormDownloadButton';
import ErrorBoundary from '../../../common/ErrorBoundary/ErrorBoundary';

const DownloadsList = ({ summaryTemplateLabel, heading, noDownloadItemsMessage, ...props }: DownloadsListProps) => {
  const { downloadsList, removeItem, actionsState } = useScorm();
  const {
    buttonLabel,
    chooseFormatLabel,
    downloadOptionsItems,
    waiverContent,
    contentDownloadSuccessMessage,
    ...tableRest
  } = props;
  const device = useDevice();
  const isRevalidating = downloadsList.isValidating && !downloadsList.isLoading;
  const tableId = useId('');

  const summary = replacePlaceholder({ count: downloadsList.data?.length }, sanitizeText(summaryTemplateLabel ?? ''));

  const tableData = useMemo(
    () =>
      downloadsList.data?.reduce(
        mapDownloadItemToTableRow(['name'], { onBinClickHandler: removeItem, itemsRemovingState: actionsState }),
        {},
      ),
    [downloadsList.data, device, actionsState],
  );

  return (
    <>
      <BasicBanner variant="primaryNavy">
        <PureHeading level={HeadingLevel.h1} className={styles.bannerText}>
          {heading}
        </PureHeading>
      </BasicBanner>
      <ErrorBoundary error={downloadsList.error} errorAsTitle>
        <Layout className={styles.contentWrapper}>
          {tableData && !Object.keys(tableData).length ? (
            !!noDownloadItemsMessage && (
              <div
                role="status"
                className={styles.noDownloadItemsMessageWrapper}
                data-testid={downloadsListIds.noDownloadItemsMessage}
              >
                <ContentBlockView contentBlock={noDownloadItemsMessage} />
              </div>
            )
          ) : (
            <>
              <div className={styles.summaryContainer} aria-describedby={tableId}>
                {isRevalidating && <RefreshLoader />}
                {downloadsList.isLoading ? (
                  <Skeleton className={styles.summarySkeleton} />
                ) : (
                  <PureRichText richtext={summary} richtextClass={styles.textContainer} />
                )}
              </div>
              <ScormDownloadButton
                buttonLabel={buttonLabel || ''}
                chooseFormatLabel={chooseFormatLabel}
                downloadOptionsItems={downloadOptionsItems}
                waiverContent={waiverContent}
                contentDownloadSuccessMessage={contentDownloadSuccessMessage}
              />
              <TableManager
                {...tableRest}
                id={tableId}
                mobileVariant="secondary"
                isInitialLoad={downloadsList.isLoading}
                isLoading={isRevalidating}
                data={tableData || {}}
                headings={DOWNLOADS_LIST_HEADINGS}
                className={styles.tableContainer}
                customClasses={{ headContainer: styles.tableHeadingContainer }}
              />
            </>
          )}
        </Layout>
      </ErrorBoundary>
    </>
  );
};

export default DownloadsList;
